import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAuth } from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';

const settings = ['Profile', 'Logout'];

// Styled components
const ModernAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#111b21',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid #e9edef',
}));

const ContactInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
  minWidth: 0,
}));

const ContactAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: '#008069',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 600,
}));

const ContactDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
}));

const ContactName = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#111b21',
  lineHeight: 1.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const ContactNumber = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: '#667781',
  lineHeight: 1.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: '#e8f5e8',
  },
  marginRight: '8px',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: '#008069',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
    transform: 'scale(1.05)',
    transition: 'all 0.2s ease',
  },
}));

const ModernMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',
    border: '1px solid #e9edef',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginTop: '8px',
  },
}));

const ModernMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '14px',
  color: '#111b21',
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: '#f0f2f5',
  },
}));

function ResponsiveChatAppBar(props) {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const { user, logout, currentConfig, changeCurrentConfig, getCurrentConfig } = useAuth();

  const [name, setName] = useState(props.contact.NombreCompleto ? props.contact.NombreCompleto : "");
  const [number, setNumber] = useState(props.contact.Celular ? props.contact.Celular : "Chat");

  useEffect(() => {
    if (props.contact.Celular === undefined) {
      setName("Chat");
      setNumber("");
    } else {
      setName(props.contact.NombreCompleto);
      setNumber(props.contact.Celular);
    }
  }, [props.contact.NombreCompleto, props.contact.Celular]);

  const navigate = useNavigate();

  const handleOpenNavMenu = (event) => {
    //check if the page end in /chat
    if (window.location.pathname.endsWith("/chat")) {
      //back to contacts list
      changeCurrentConfig({ currentPage: "contacts", currentContact: null });
      const messagesContainer = document.getElementById("messagesContainer");
      const contactsContainer = document.getElementById("contactsContainer");
      if (messagesContainer) messagesContainer.classList.add("hidden");
      if (contactsContainer) contactsContainer.classList.remove("hidden");
      //navigate to chat
      navigate('/');
    } else {
      setAnchorElNav(event.currentTarget);
    }
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = (page) => {
    const path = user.roles.includes("ROLE_ADMIN") ? '/admin/' : '/dashboard/';
    navigate(path + page)
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = (setting) => {
    if (setting === 'Logout') {
      logout();
    }
    else if (setting === 'Profile') {
      const path = user?.roles?.includes("ROLE_ADMIN") ? '/admin/settings' : '/dashboard/settings';
      navigate(path);
    }
    setAnchorElUser(null);
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name || name === "Chat" || name === process.env.REACT_APP_PHONE_NAME) return "C";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <ModernAppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ padding: '8px 16px' }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', flex: 1 }}>
            <BackButton
              size="medium"
              aria-label="back to contacts"
              onClick={handleOpenNavMenu}
            >
              <ArrowBackIcon />
            </BackButton>

            <ContactInfo>
              <ContactAvatar>
                {getInitials(name)}
              </ContactAvatar>
              
              <ContactDetails>
                <ContactName>
                  {name || (process.env.REACT_APP_PHONE_NAME || "Chat")}
                </ContactName>
                {number && (
                  <ContactNumber>
                    {number}
                  </ContactNumber>
                )}
              </ContactDetails>
            </ContactInfo>
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Configuración" arrow>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <UserAvatar>
                  {user?.username ? user.username.charAt(0).toUpperCase() : "U"}
                </UserAvatar>
              </IconButton>
            </Tooltip>
            <ModernMenu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <ModernMenuItem key={setting} onClick={() => handleCloseUserMenu(setting)}>
                  <Typography textAlign="center" sx={{ fontSize: '14px' }}>
                    {setting}
                  </Typography>
                </ModernMenuItem>
              ))}
            </ModernMenu>
          </Box>
        </Toolbar>
      </Container>
    </ModernAppBar>
  );
}

export default ResponsiveChatAppBar;